"use client";
import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import axios from "axios";
// @ts-ignore
import { API_BASE_URL } from "../../../../src/App.js";
import { useSelector } from "react-redux";
import { COUNTRY_OPTIONS, INDIA_STATES_AND_UT, isIndiaCountry } from "../../../constants/countryStates";

interface Customer {
  id: number;
  companyName: string;
  companyMobile: string;
  personName: string;
  personMobile: string;
  address: string;
  country: string;
  state: string;
  pincode?: string | null;
  gst?: string | null;
}

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (customer: Customer) => void;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({ open, onClose, onAdd }) => {
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const empty = {
    companyName: "",
    companyMobile: "",
    personName: "",
    personMobile: "",
    address: "",
    country: "India",
    state: "",
    pincode: "",
    gst: "",
  };
  const [formData, setFormData] = useState(empty);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  const set = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formData.companyName.trim()) e.companyName = "Company name is required";
    if (!formData.companyMobile.trim()) e.companyMobile = "Company mobile is required";
    if (!formData.personName.trim()) e.personName = "Person name is required";
    if (!formData.personMobile.trim()) e.personMobile = "Person mobile is required";
    if (!formData.address.trim()) e.address = "Address is required";
    if (!formData.state.trim()) e.state = "State is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        companyName: formData.companyName.trim(),
        companyMobile: formData.companyMobile.trim(),
        personName: formData.personName.trim(),
        personMobile: formData.personMobile.trim(),
        address: formData.address.trim(),
        country: formData.country.trim() || "India",
        state: formData.state.trim(),
        pincode: formData.pincode.trim() || null,
        gst: isIndiaCountry(formData.country) && formData.gst.trim() ? formData.gst.trim() : null,
      };
      const res = await axios.post(
        `${API_BASE_URL}account/${id}/customer/create`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      onAdd({ id: res.data.id || Date.now(), ...payload });
      setFormData(empty);
      setErrors({});
      onClose();
    } catch (err: any) {
      setErrors({ submit: err.response?.data?.message || "Error creating customer" });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [open]);

  const field = (label: string, key: string, placeholder: string, required = false, type = "text") => (
    <div>
      <Label className="font-semibold text-gray-700 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        value={(formData as any)[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        type={type}
        className={`border-2 rounded-lg ${errors[key] ? "border-red-400" : "border-gray-300"}`}
      />
      {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <>
      {open && <div className="fixed inset-0 bg-black bg-opacity-50 z-[70]" onClick={onClose} />}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-[75] transform transition-all duration-300 w-full md:w-[40vw] md:h-auto max-h-screen md:max-h-[80vh] rounded-t-2xl md:rounded-lg flex flex-col overflow-hidden ${
          open ? "translate-y-0 md:scale-100 pointer-events-auto opacity-100 visible" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none invisible md:visible"
        }`}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b-2 border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">➕ Add Customer</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {field("Company Name", "companyName", "Company / Business name", true)}
          {field("Company Mobile", "companyMobile", "Company contact number", true)}
          {field("Person Name", "personName", "Contact person name", true)}
          {field("Person Mobile", "personMobile", "Contact person mobile", true)}
          {field("Address", "address", "Full address", true)}
          <div>
            <Label className="font-semibold text-gray-700 mb-1 block">
              Country <span className="text-red-500">*</span>
            </Label>
            <select
              className={`w-full border-2 rounded-lg px-3 py-2 text-sm ${errors.country ? "border-red-400" : "border-gray-300"}`}
              value={formData.country}
              onChange={(e) => {
                const country = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  country,
                  gst: isIndiaCountry(country) ? prev.gst : "",
                }));
              }}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="font-semibold text-gray-700 mb-1 block">
              State / UT <span className="text-red-500">*</span>
            </Label>
            {isIndiaCountry(formData.country) ? (
              <select
                className={`w-full border-2 rounded-lg px-3 py-2 text-sm ${errors.state ? "border-red-400" : "border-gray-300"}`}
                value={formData.state}
                onChange={(e) => set("state", e.target.value)}
              >
                <option value="">Select state / UT</option>
                {INDIA_STATES_AND_UT.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            ) : (
              <Input
                value={formData.state}
                onChange={(e) => set("state", e.target.value)}
                placeholder="State or province"
                className={`border-2 rounded-lg ${errors.state ? "border-red-400" : "border-gray-300"}`}
              />
            )}
            {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {field("Pincode", "pincode", "Pincode (optional)")}
            {isIndiaCountry(formData.country) ? (
              <div>
                <Label className="font-semibold text-gray-700 mb-1 block">GSTIN (optional)</Label>
                <Input
                  value={formData.gst}
                  onChange={(e) => set("gst", e.target.value)}
                  placeholder="15-character GSTIN"
                  className="border-2 rounded-lg border-gray-300"
                />
              </div>
            ) : (
              <div />
            )}
          </div>

          {errors.submit && (
            <p className="text-red-600 text-sm font-semibold">{errors.submit}</p>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose} className="border-2 border-gray-400 font-bold">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit as any}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? "Saving..." : "Save Customer"}
          </Button>
        </div>
      </div>
    </>
  );
};
